import { useNavigate } from 'react-router';

import { useTranslation } from 'react-i18next';

import useAuth from '@/hooks/useAuth';

import { FeaturePath, useFeaturePermission } from '@/utils/permissions';

import ConfirmationModal from '@/components/popups/ConfirmationModal';

/**
 * Higher‑order component to prevent unauthorized access to a route.
 *
 * @param Component The component to wrap.
 * @param featurePath The path to the feature in the feature set.
 */
export const withRoles = <T extends React.ElementType>(
  Component: T,
  featurePath: FeaturePath,
) => {
  const displayName =
    typeof Component === 'function'
      ? (Component.displayName ?? Component.name)
      : Component;
  const WrappedComponent = (props: React.ComponentProps<T>) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: auth } = useAuth();
    const userInternational = auth?.isInternationalUser;
    const featureEnabled = useFeaturePermission(featurePath);

    if (userInternational && !featureEnabled) {
      return (
        <ConfirmationModal
          bodyText={t('LoadingResourceError.unauthorizedParagraph')}
          headerText={t('GenericError.unauthorized')}
          isError
          isOpen
          onClose={() => navigate('/')}
          primaryButtonHandler={() => navigate('/')}
          primaryButtonText={t('Button.returnToHomepage')}
          primaryButtonVariant="destructive"
        />
      );
    } else if (!featureEnabled) {
      return (
        <ConfirmationModal
          bodyText={t('UserNotAssignedToRoleError.errorParagraph')}
          headerText={t('GenericError.unauthorized')}
          isError
          isOpen
          onClose={() => navigate('/')}
          primaryButtonHandler={() => navigate('/')}
          primaryButtonText={t('Button.returnToHomepage')}
          primaryButtonVariant="destructive"
        />
      );
    } else {
      return <Component {...props} />;
    }
  };

  WrappedComponent.displayName = `withRoles(${displayName})`;
  return WrappedComponent;
};
